import { useLayoutEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { formatMoneyInput, parseMoneyInput } from "../formatting";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "inputMode" | "value" | "defaultValue" | "onChange" | "onBlur" | "onPaste" | "onKeyDown"> & {
  value: string;
  onChange: (value: string) => void;
};

export function MoneyInput({ value, onChange, min = "0", max, ...props }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const [editing, setEditing] = useState(false);
  const displayed = formatMoneyInput(value, editing);

  useLayoutEffect(() => {
    const field = input.current;
    if (!field) return;
    if (caret.current !== null) {
      field.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
    const amount = parseMoneyInput(value);
    const outOfRange = amount !== null && (Number(amount) < Number(min) || (max !== undefined && Number(amount) > Number(max)));
    field.setCustomValidity(value && (amount === null || outOfRange) ? "Geçerli aralıkta bir tutar girin. Örnek: 125,000.00" : "");
  }, [value, min, max, editing]);

  return <input {...props} ref={input} type="text" inputMode="decimal" value={displayed}
    placeholder={props.placeholder || "0.00"}
    onChange={event => {
      const text = event.target.value;
      const native = event.nativeEvent as InputEvent;
      if (native.data && native.inputType !== "insertFromPaste" && /[^\d.]/.test(native.data)) return;
      const ungrouped = text.replaceAll(",", "");
      const plain = ungrouped.startsWith(".") ? `0${ungrouped}` : ungrouped;
      if (!/^\d*(\.\d{0,2})?$/.test(plain)) return;
      const next = formatMoneyInput(plain, true);
      let remaining = text.slice(0, event.target.selectionStart ?? text.length).replaceAll(",", "").length;
      if (plain !== ungrouped) remaining++;
      let position = 0;
      while (position < next.length && remaining > 0) {
        if (next[position] !== ",") remaining--;
        position++;
      }
      caret.current = position;
      setEditing(true);
      onChange(plain);
    }}
    onPaste={event => {
      if (parseMoneyInput(event.clipboardData.getData("text")) === null) event.preventDefault();
    }}
    onKeyDown={event => {
      const field = event.currentTarget;
      const start = field.selectionStart ?? 0;
      if (start !== field.selectionEnd) return;
      // Deleting beside a grouping comma should delete a digit, not stall.
      if (event.key === "Backspace" && field.value[start - 1] === ",") field.setSelectionRange(start - 1, start - 1);
      if (event.key === "Delete" && field.value[start] === ",") field.setSelectionRange(start + 1, start + 1);
    }}
    onBlur={() => {
      setEditing(false);
      const amount = parseMoneyInput(value.endsWith(".") ? `${value}00` : value);
      if (editing && amount !== null && amount !== value) onChange(amount);
    }}
  />;
}
